import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Building, Home, Users, DollarSign, TrendingUp, TrendingDown,
    ArrowUpRight, ArrowDownRight, Loader2, Calendar, MapPin,
    PieChart, BarChart3, Activity
} from 'lucide-react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import apiClient from '@/services/apiClient';

interface PortfolioStats {
    totalProperties: number;
    totalUnits: number;
    occupiedUnits: number;
    vacantUnits: number;
    occupancyRate: number;
    totalRevenue: number;
    monthlyRevenue: number;
    avgRentPerUnit: number;
    totalTenants: number;
    activeLeases: number;
    expiringLeases: number;
}

interface PropertyPerformance {
    id: string;
    name: string;
    units: number;
    occupied: number;
    revenue: number;
    expenses: number;
    roi: number;
}

const PortfolioOverview: React.FC = () => {
    const navigate = useNavigate();
    const { properties, units, tenants } = useLandlordStore();
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState<PortfolioStats | null>(null);
    const [propertyPerformance, setPropertyPerformance] = useState<PropertyPerformance[]>([]);

    useEffect(() => {
        fetchPortfolioData();
    }, []);

    const fetchPortfolioData = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/landlord/portfolio/stats');
            setStats(response.data.stats);
            setPropertyPerformance(response.data.propertyPerformance);
        } catch (err) {
            console.error('Failed to fetch portfolio data:', err);
            // Use calculated data for demo
            calculatePortfolioStats();
        } finally {
            setLoading(false);
        }
    };

    const calculatePortfolioStats = () => {
        const totalProperties = properties.length;
        const totalUnits = units.length;
        const occupiedUnits = units.filter(u => u.status === 'Occupied').length;
        const vacantUnits = units.filter(u => u.status === 'Available').length;
        const occupancyRate = totalUnits > 0 ? (occupiedUnits / totalUnits) * 100 : 0;
        const totalTenants = tenants.filter(t => t.status === 'Active').length;
        const activeLeases = tenants.filter(t => t.status === 'Active').length;
        const expiringLeases = tenants.filter(t => {
            if (!t.lease_end) return false;
            const endDate = new Date(t.lease_end);
            const today = new Date();
            const daysUntilExpiry = (endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
            return daysUntilExpiry <= 60 && daysUntilExpiry >= 0;
        }).length;

        // Calculate revenue estimates
        const monthlyRevenue = occupiedUnits * 15000; // Average rent estimate
        const totalRevenue = monthlyRevenue * 12;
        const avgRentPerUnit = occupiedUnits > 0 ? monthlyRevenue / occupiedUnits : 0;

        setStats({
            totalProperties,
            totalUnits,
            occupiedUnits,
            vacantUnits,
            occupancyRate,
            totalRevenue,
            monthlyRevenue,
            avgRentPerUnit,
            totalTenants,
            activeLeases,
            expiringLeases
        });

        // Calculate property performance
        const perf = properties.map(property => {
            const propUnits = units.filter(u => u.property_id === property.id);
            const propOccupied = propUnits.filter(u => u.status === 'Occupied').length;
            const revenue = propOccupied * 15000;
            const expenses = propUnits.length * 5000; // Estimate
            const roi = revenue > 0 ? ((revenue - expenses) / expenses) * 100 : 0;

            return {
                id: property.id,
                name: property.name,
                units: propUnits.length,
                occupied: propOccupied,
                revenue,
                expenses,
                roi
            };
        });
        setPropertyPerformance(perf);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">Portfolio Overview</h1>
                <p className="text-sm text-light-subtle dark:text-dark-subtle">
                    High-level view of your entire property portfolio performance
                </p>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Total Properties</p>
                            <p className="text-2xl font-bold text-light-text dark:text-dark-text">{stats?.totalProperties || 0}</p>
                        </div>
                        <Building className="text-primary" size={24} />
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Total Units</p>
                            <p className="text-2xl font-bold text-light-text dark:text-dark-text">{stats?.totalUnits || 0}</p>
                        </div>
                        <Home className="text-primary" size={24} />
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Occupancy Rate</p>
                            <p className="text-2xl font-bold text-light-text dark:text-dark-text">{stats?.occupancyRate?.toFixed(1) || 0}%</p>
                        </div>
                        <PieChart className={stats && stats.occupancyRate >= 80 ? 'text-green-500' : 'text-orange-500'} size={24} />
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Monthly Revenue</p>
                            <p className="text-2xl font-bold text-light-text dark:text-dark-text">KES {(stats?.monthlyRevenue || 0).toLocaleString()}</p>
                        </div>
                        <DollarSign className="text-green-500" size={24} />
                    </div>
                </div>
            </div>

            {/* Additional Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Occupied Units</p>
                            <p className="text-xl font-bold text-light-text dark:text-dark-text">{stats?.occupiedUnits || 0}</p>
                        </div>
                        <TrendingUp className="text-green-500" size={20} />
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Vacant Units</p>
                            <p className="text-xl font-bold text-light-text dark:text-dark-text">{stats?.vacantUnits || 0}</p>
                        </div>
                        <TrendingDown className="text-orange-500" size={20} />
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Active Tenants</p>
                            <p className="text-xl font-bold text-light-text dark:text-dark-text">{stats?.totalTenants || 0}</p>
                        </div>
                        <Users className="text-primary" size={20} />
                    </div>
                </div>
                <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Expiring Leases (60 days)</p>
                            <p className="text-xl font-bold text-orange-500">{stats?.expiringLeases || 0}</p>
                        </div>
                        <Calendar className="text-orange-500" size={20} />
                    </div>
                </div>
            </div>

            {/* Property Performance Table */}
            <div className="bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border">
                <div className="p-4 border-b border-light-border dark:border-dark-border">
                    <h2 className="font-bold text-light-text dark:text-dark-text">Property Performance</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-light-bg dark:bg-dark-bg">
                            <tr>
                                <th className="px-4 py-3 text-left text-sm font-medium text-light-subtle dark:text-dark-subtle">Property</th>
                                <th className="px-4 py-3 text-right text-sm font-medium text-light-subtle dark:text-dark-subtle">Units</th>
                                <th className="px-4 py-3 text-right text-sm font-medium text-light-subtle dark:text-dark-subtle">Occupied</th>
                                <th className="px-4 py-3 text-right text-sm font-medium text-light-subtle dark:text-dark-subtle">Occupancy</th>
                                <th className="px-4 py-3 text-right text-sm font-medium text-light-subtle dark:text-dark-subtle">Revenue</th>
                                <th className="px-4 py-3 text-right text-sm font-medium text-light-subtle dark:text-dark-subtle">Expenses</th>
                                <th className="px-4 py-3 text-right text-sm font-medium text-light-subtle dark:text-dark-subtle">ROI</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-light-border dark:divide-dark-border">
                            {propertyPerformance.map(property => (
                                <tr key={property.id} className="hover:bg-light-bg dark:hover:bg-dark-bg">
                                    <td className="px-4 py-3 text-light-text dark:text-dark-text font-medium">{property.name}</td>
                                    <td className="px-4 py-3 text-right text-light-text dark:text-dark-text">{property.units}</td>
                                    <td className="px-4 py-3 text-right text-light-text dark:text-dark-text">{property.occupied}</td>
                                    <td className="px-4 py-3 text-right">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            (property.occupied / property.units) * 100 >= 80
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                        }`}>
                                            {((property.occupied / property.units) * 100).toFixed(0)}%
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right text-green-600 dark:text-green-400">KES {property.revenue.toLocaleString()}</td>
                                    <td className="px-4 py-3 text-right text-red-600 dark:text-red-400">KES {property.expenses.toLocaleString()}</td>
                                    <td className="px-4 py-3 text-right">
                                        <span className={`font-medium ${property.roi >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {property.roi >= 0 ? '+' : ''}{property.roi.toFixed(1)}%
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                    onClick={() => navigate('/landlord/properties/new')}
                    className="flex items-center justify-center gap-2 p-4 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                    <Building size={20} className="text-primary" />
                    <span className="font-medium text-light-text dark:text-dark-text">Add Property</span>
                </button>
                <button
                    onClick={() => navigate('/landlord/vacancies/new')}
                    className="flex items-center justify-center gap-2 p-4 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                    <Home size={20} className="text-primary" />
                    <span className="font-medium text-light-text dark:text-dark-text">Post Vacancy</span>
                </button>
                <button
                    onClick={() => navigate('/landlord/tenants/register')}
                    className="flex items-center justify-center gap-2 p-4 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                    <Users size={20} className="text-primary" />
                    <span className="font-medium text-light-text dark:text-dark-text">Register Tenant</span>
                </button>
            </div>
        </div>
    );
};

export default PortfolioOverview;